import { useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, Loader2, FileSpreadsheet, CalendarRange, CheckSquare, Square } from "lucide-react";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from "date-fns";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: "daily", label: "Daily Lottery", desc: "Sales, inventory, machine totals, closing numbers" },
  { key: "lvm", label: "LVM", desc: "Cash count + prize reports for LVM-1 / LVM-2" },
  { key: "coupons", label: "Coupons / Others", desc: "Vendor coupon ledger" },
  { key: "sp", label: "Store Paperwork", desc: "Z-Report: Debit, Credit, Suppliers, Fuel, Lottery" },
];

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
  { key: "custom", label: "Custom Range" },
];

function presetRange(key, anchor = new Date()) {
  switch (key) {
    case "today":
      return { start: anchor, end: anchor };
    case "week":
      return {
        start: startOfWeek(anchor, { weekStartsOn: 1 }),
        end: endOfWeek(anchor, { weekStartsOn: 1 }),
      };
    case "month":
      return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
    case "year":
      return { start: startOfYear(anchor), end: endOfYear(anchor) };
    default:
      return { start: anchor, end: anchor };
  }
}

export default function ExportReports() {
  const today = useMemo(() => new Date(), []);
  const [preset, setPreset] = useState("month");
  const [customStart, setCustomStart] = useState(format(today, "yyyy-MM-dd"));
  const [customEnd, setCustomEnd] = useState(format(today, "yyyy-MM-dd"));
  const [selected, setSelected] = useState(() => new Set(SECTIONS.map((s) => s.key)));
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => {
    if (preset === "custom") {
      return { start: customStart, end: customEnd };
    }
    const r = presetRange(preset, today);
    return { start: format(r.start, "yyyy-MM-dd"), end: format(r.end, "yyyy-MM-dd") };
  }, [preset, customStart, customEnd, today]);

  const toggle = (key) => {
    setSelected((s) => {
      const next = new Set(s);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const allOn = selected.size === SECTIONS.length;
  const noneOn = selected.size === 0;

  const handleExport = async () => {
    if (noneOn) {
      toast.error("Select at least one section to export");
      return;
    }
    if (!range.start || !range.end) {
      toast.error("Pick a date range");
      return;
    }
    if (range.start > range.end) {
      toast.error("Start date must be on or before End date");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/export/workbook`, {
        params: {
          start: range.start,
          end: range.end,
          sections: Array.from(selected).join(","),
        },
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lottery-export_${range.start}_to_${range.end}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${range.start} → ${range.end}`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Export failed";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 max-w-4xl"
      data-testid="exports-page"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center">
          <FileSpreadsheet className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="font-mono text-2xl font-bold tracking-tight" data-testid="exports-title">
            Reports &amp; Exports
          </h1>
          <p className="text-xs text-card-foreground/70 font-mono">
            Download Excel (.xlsx) workbooks for any combination of sections and date range.
          </p>
        </div>
      </div>

      {/* Section picker */}
      <Card data-testid="exports-sections-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
          <CardTitle className="font-mono text-sm">1. Choose sections</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set(SECTIONS.map((s) => s.key)))}
              disabled={allOn}
              className="text-xs"
              data-testid="exports-select-all"
            >
              Select all
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set())}
              disabled={noneOn}
              className="text-xs"
              data-testid="exports-clear-all"
            >
              Clear
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {SECTIONS.map((s) => {
            const on = selected.has(s.key);
            const Icon = on ? CheckSquare : Square;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggle(s.key)}
                className={`text-left flex items-start gap-3 p-3 rounded-lg border transition-all ${
                  on
                    ? "border-primary/60 bg-primary/10"
                    : "border-border/40 bg-secondary/30 hover:bg-secondary/50"
                }`}
                data-testid={`exports-section-${s.key}`}
              >
                <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${on ? "text-primary" : "text-muted-foreground"}`} />
                <div>
                  <p className="font-mono font-bold text-sm text-card-foreground">{s.label}</p>
                  <p className="text-[11px] text-muted-foreground font-mono leading-tight">{s.desc}</p>
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Range picker */}
      <Card data-testid="exports-range-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-mono text-sm flex items-center gap-2">
            <CalendarRange className="w-4 h-4" />
            2. Choose date range
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                variant={preset === p.key ? "default" : "outline"}
                size="sm"
                onClick={() => setPreset(p.key)}
                className="btn-3d"
                data-testid={`exports-preset-${p.key}`}
              >
                {p.label}
              </Button>
            ))}
          </div>

          {preset === "custom" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
              <div>
                <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1 block">
                  Start
                </label>
                <Input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="font-mono"
                  data-testid="exports-custom-start"
                />
              </div>
              <div>
                <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1 block">
                  End
                </label>
                <Input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="font-mono"
                  data-testid="exports-custom-end"
                />
              </div>
            </div>
          ) : (
            <p className="text-xs font-mono text-muted-foreground" data-testid="exports-range-preview">
              <span className="text-card-foreground font-bold">{range.start}</span>
              {" → "}
              <span className="text-card-foreground font-bold">{range.end}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Export button */}
      <Card className="border-primary/40 bg-primary/5" data-testid="exports-go-card">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-sm text-card-foreground">
              <strong className="text-primary">{selected.size}</strong>{" "}
              section{selected.size !== 1 && "s"} ·{" "}
              <strong className="text-primary">{range.start}</strong> → {" "}
              <strong className="text-primary">{range.end}</strong>
            </p>
            <p className="text-[10px] font-mono text-card-foreground/70 italic">
              One sheet per (section, date) pair · empty dates are skipped automatically.
            </p>
          </div>
          <Button
            onClick={handleExport}
            disabled={loading || noneOn}
            className="btn-3d"
            data-testid="exports-download-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Building workbook…
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download Excel (.xlsx)
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
}
